import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Landmark, Plus, Loader2, Trash2, Search, Star, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_STYLE = {
  'Intro': 'bg-slate-100 text-slate-700 border-slate-200',
  'Deck shared': 'bg-sky-50 text-sky-700 border-sky-200',
  'Meeting': 'bg-violet-50 text-violet-700 border-violet-200',
  'Term sheet': 'bg-amber-50 text-amber-700 border-amber-200',
  'Closed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Passed': 'bg-rose-50 text-rose-600 border-rose-200',
};

export const FundraisingTrack = ({ biotechId, onCountChange }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], by_stage: {}, stages: [] });
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [investors, setInvestors] = useState([]);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState('');
  const [noteEdit, setNoteEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/fundraising/${biotechId}`, { withCredentials: true }); setData(r.data); onCountChange?.((r.data?.items || []).length); }
    catch { /* keep empty */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [biotechId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPicker = async () => {
    setPickerOpen(true);
    if (investors.length) return;
    try { const r = await axios.get(`${API}/investors?limit=5000`, { withCredentials: true }); setInvestors(r.data.items || []); }
    catch { toast.error('Could not load investors'); }
  };

  const onTrack = useMemo(() => new Set(data.items.map(i => i.investor_id)), [data.items]);
  const candidates = useMemo(() => {
    const s = q.trim().toLowerCase();
    return investors.filter(i => !onTrack.has(i.id) && (!s || [i.firm_name, i.name, i.email, i.location, i.investor_type].join(' ').toLowerCase().includes(s)))
      .sort((a, b) => (b.highlighted ? 1 : 0) - (a.highlighted ? 1 : 0)).slice(0, 40);
  }, [investors, q, onTrack]);

  const add = async (inv) => {
    setAdding(inv.id);
    try { await axios.post(`${API}/fundraising/${biotechId}`, { investor_id: inv.id }, { withCredentials: true }); toast.success(`${inv.firm_name || inv.name} added to fundraising track`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setAdding(''); }
  };
  const setStage = async (intro, stage) => {
    try { await axios.patch(`${API}/fundraising/${biotechId}/${intro.id}`, { stage }, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update stage'); }
  };
  const saveNote = async () => {
    try { await axios.patch(`${API}/fundraising/${biotechId}/${noteEdit.id}`, { notes: noteEdit.notes, next_step: noteEdit.next_step, next_step_date: noteEdit.next_step_date }, { withCredentials: true }); setNoteEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };
  const remove = async (intro) => {
    try { await axios.delete(`${API}/fundraising/${biotechId}/${intro.id}`, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove'); }
  };

  return (
    <div data-testid="fundraising-track">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {(data.stages || []).map(s => (
            <Badge key={s} variant="outline" className={`text-[10px] ${STAGE_STYLE[s] || ''}`} data-testid={`fundraising-stage-count-${s.replace(/\s+/g, '-').toLowerCase()}`}>{s} · {data.by_stage?.[s] || 0}</Badge>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => navigate('/investors')}><ExternalLink className="w-3 h-3 mr-1" /> Investors directory</Button>
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openPicker} data-testid="fundraising-add-btn"><Plus className="w-3 h-3 mr-1" /> Add investor</Button>
        </div>
      </div>

      {loading ? <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
        : data.items.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400" data-testid="fundraising-empty">
            <Landmark className="w-6 h-6 mx-auto mb-2 text-slate-300" />No investors on this fundraising track yet. Add one to start tracking Intro → Deck shared → Meeting → Term sheet.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {data.items.map(intro => {
              const inv = intro.investor || {};
              return (
                <div key={intro.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 bg-white hover:bg-slate-50/60" data-testid={`fundraising-row-${intro.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                      {inv.highlighted && <Star className="w-3 h-3 fill-amber-400 text-amber-500 shrink-0" />}{inv.firm_name || inv.name || intro.investor_name}
                      {inv.investor_type && <span className="font-normal text-[10px] text-slate-500">· {inv.investor_type}</span>}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">{[inv.name !== inv.firm_name ? inv.name : '', inv.email, inv.location].filter(Boolean).join(' · ')}</p>
                    {(intro.notes || intro.next_step) && <p className="text-[11px] text-slate-600 mt-0.5 truncate">{intro.next_step ? <span className="text-violet-700">Next: {intro.next_step}{intro.next_step_date ? ` (${intro.next_step_date})` : ''} · </span> : null}{intro.notes}</p>}
                  </div>
                  <select value={intro.stage} onChange={e => setStage(intro, e.target.value)} className={`h-7 text-[11px] rounded-md border px-2 ${STAGE_STYLE[intro.stage] || ''}`} data-testid={`fundraising-stage-select-${intro.id}`}>
                    {(data.stages || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className="text-[10px] text-slate-400 w-20 text-right hidden md:block">{intro.updated_at ? new Date(intro.updated_at).toLocaleDateString() : ''}</span>
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setNoteEdit({ id: intro.id, notes: intro.notes || '', next_step: intro.next_step || '', next_step_date: intro.next_step_date || '' })} data-testid={`fundraising-note-${intro.id}`}>Notes</Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500 hover:bg-rose-50" onClick={() => remove(intro)} data-testid={`fundraising-remove-${intro.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-lg" data-testid="fundraising-picker">
          <DialogHeader><DialogTitle>Add investor to fundraising track</DialogTitle></DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search firm, contact, type, location…" className="pl-9 h-9 text-sm" data-testid="fundraising-picker-search" />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
            {candidates.length === 0 && <p className="text-xs text-slate-400 text-center py-6">{investors.length ? 'No matching investors' : 'Loading…'}</p>}
            {candidates.map(inv => (
              <div key={inv.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`fundraising-candidate-${inv.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-900 truncate flex items-center gap-1">{inv.highlighted && <Star className="w-3 h-3 fill-amber-400 text-amber-500" />}{inv.firm_name || inv.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{[inv.investor_type, inv.location, inv.email].filter(Boolean).join(' · ')}</p>
                </div>
                <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => add(inv)} disabled={adding === inv.id} data-testid={`fundraising-candidate-add-${inv.id}`}>
                  {adding === inv.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!noteEdit} onOpenChange={(v) => { if (!v) setNoteEdit(null); }}>
        <DialogContent className="max-w-md" data-testid="fundraising-note-dialog">
          <DialogHeader><DialogTitle>Notes & next step</DialogTitle></DialogHeader>
          {noteEdit && (
            <div className="space-y-3">
              <Textarea value={noteEdit.notes} onChange={e => setNoteEdit(n => ({ ...n, notes: e.target.value }))} rows={3} placeholder="Conversation notes…" className="text-sm" data-testid="fundraising-note-input" />
              <div className="grid grid-cols-3 gap-2">
                <Input className="col-span-2 h-8 text-sm" value={noteEdit.next_step} onChange={e => setNoteEdit(n => ({ ...n, next_step: e.target.value }))} placeholder="Next step (e.g. send data room)" data-testid="fundraising-next-step-input" />
                <Input type="date" className="h-8 text-sm" value={noteEdit.next_step_date} onChange={e => setNoteEdit(n => ({ ...n, next_step_date: e.target.value }))} data-testid="fundraising-next-step-date" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNoteEdit(null)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveNote} data-testid="fundraising-note-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
